import { createSlice } from '@reduxjs/toolkit';

import {
  DroppedAddOns,
  GetPlanOffersPayload,
  PlanCharacteristics,
  ShoppingCartResponse,
  TransitionSchedule,
  TransitionScheduleOutput,
} from '@mfe/shared/schema-types';

import { FetchWithErrorsQuery } from '../graphql';
import { EicMessage } from '../types';

export enum ChangePlanSteps {
  PLAN_SELECTION = 'PLAN_SELECTION',
  ORDER_REVIEW = 'ORDER_REVIEW',
  OPTIONAL_SERVICE_WARNING = 'OPTIONAL_SERVICE_WARNING',
  SELECT_SCHEDULE = 'SELECT_SCHEDULE',
  ORDER_OUTCOME = 'ORDER_OUTCOME',
}

type ChangePlanOffersState = {
  loading: boolean;
  planOffersData: GetPlanOffersPayload[] | null;
  droppedAddOns: DroppedAddOns[];
  error: FetchWithErrorsQuery['errors'] | null;
  cart: ShoppingCartResponse;
  selectedPlanId: string;
  step: ChangePlanSteps;
  requiresEquipmentChange: boolean;
  orderSuccess: boolean;
  hideBroadbandLabels: boolean;
  installationAppointment: Omit<TransitionScheduleOutput, 'workOrderScheduled'> | null;
  hasPendingTransition: boolean;
  hasPendingTransitionLoading: boolean;
  hasPendingTransitionError: FetchWithErrorsQuery['errors'] | null;
  hasActivationError: boolean;
  hasEquipmentForSelfInstall: boolean;
  isEicChecked: boolean;
  hasAttemptedSubmit: boolean;
  eicMessageLoading: boolean;
  eicMessage: EicMessage | null;
  scrollToConsent: boolean;
  characteristics: PlanCharacteristics;
};

export type TransitionScheduleInput = {
  notes?: string;
  appointment?: TransitionSchedule;
};

export const defaultCart = {
  droppedAddOns: [],
  outdoorUnit: {
    currentModel: '',
    newModel: '',
  },
  cartItems: {
    plan: {
      name: '',
    },
    bundledAddons: [],
  },
  id: '',
  leaseFeeShortName: '',
  prices: {
    monthly: {
      lineItems: undefined,
      totalWithPromos: undefined,
      totalWithoutPromos: undefined,
    },
    oneTime: {
      lease: undefined,
      totalWithPromos: undefined,
      totalWithoutPromos: undefined,
      lineItems: {
        promo: undefined,
      },
      feesAndDiscounts: undefined,
    },
  },
  status: '',
  eicRequired: false,
  requiresEquipmentChange: false,
  hasEquipmentForSelfInstall: false,
};

export const initialChangePlanState: ChangePlanOffersState = {
  loading: false,
  error: null,
  step: ChangePlanSteps.PLAN_SELECTION,
  requiresEquipmentChange: false,
  droppedAddOns: [],
  orderSuccess: false,
  selectedPlanId: '',
  installationAppointment: null,
  hideBroadbandLabels: false,
  planOffersData: null,
  cart: defaultCart,
  hasPendingTransition: false,
  hasPendingTransitionError: null,
  hasPendingTransitionLoading: false,
  hasActivationError: false,
  hasEquipmentForSelfInstall: false,
  isEicChecked: false,
  hasAttemptedSubmit: false,
  eicMessageLoading: false,
  eicMessage: null,
  scrollToConsent: false,
  characteristics: {} as PlanCharacteristics,
};

export const changePlanSlice = createSlice({
  name: 'changePlan',
  initialState: initialChangePlanState,
  reducers: {
    getPlans: (state) => {
      state.loading = true;
      state.error = null;
    },
    setPlans: (state, { payload }: { payload: GetPlanOffersPayload[] }) => {
      state.loading = false;
      state.planOffersData = payload;
      state.error = null;
    },
    refetchPlans: (state) => {
      state.loading = true;
      state.error = null;
    },
    addPlanToCart: (state, { payload }: { payload: { newProductTypeId: string } }) => {
      state.loading = true;
      state.error = null;
    },
    saveCart: (state, { payload }: { payload: ShoppingCartResponse }) => {
      state.loading = false;
      state.cart = payload;
      state.droppedAddOns = payload.droppedAddOns;
      state.requiresEquipmentChange = payload.requiresEquipmentChange;
      state.error = null;
    },
    submitOrder: (state, { payload }: { payload: TransitionScheduleInput | undefined }) => {
      state.loading = true;
      state.error = null;
    },
    setOrderSuccess: (
      state,
      {
        payload,
      }: {
        payload: {
          isSuccess: boolean;
          appointment: TransitionScheduleOutput | null;
        };
      }
    ) => {
      state.loading = false;
      state.orderSuccess = payload.isSuccess;

      if (payload.appointment) {
        state.installationAppointment = payload.appointment;
      }

      state.error = null;
    },
    setChangePlanError: (state, { payload }) => {
      state.loading = false;
      state.error = payload;
    },
    setChangePlanStep: (state, { payload }: { payload: ChangePlanSteps }) => {
      state.step = payload;
    },
    setSelectedPlanId: (state, { payload }) => {
      state.selectedPlanId = payload;
    },
    resetChangePlan: (state) => {
      state.loading = false;
      state.planOffersData = null;
      state.requiresEquipmentChange = false;
      state.error = null;
    },
    toggleBroadbandLabels: (state) => {
      state.hideBroadbandLabels = !state.hideBroadbandLabels;
    },
    resetHideBroadbandLabels: (state) => {
      state.hideBroadbandLabels = false;
    },
    getHasPendingTransition: (state) => {
      state.hasPendingTransitionLoading = true;
    },
    setHasPendingTransition: (state, action) => {
      state.hasPendingTransition = action.payload;
      state.hasPendingTransitionLoading = false;
    },
    setHasActivationError: (state, { payload }) => {
      state.hasActivationError = payload;
    },
    // This was moved here from the old planInfoSlice
    updateDataStillLoading: (state, { payload }: { payload: boolean }) => {
      state.hasPendingTransition = payload;
    },
    setHasPendingTransitionError: (state, action) => {
      state.hasPendingTransitionError = action.payload;
      state.hasPendingTransitionLoading = false;
    },
    // ------------------- Disclosures add-ons -------------------
    getEicMessages: (state, { payload }: { payload: { cartId: string } }) => {
      state.eicMessageLoading = true;
      state.error = null;
      state.cart = { ...state.cart, id: payload.cartId };
    },
    setEicMessages: (state, action) => {
      state.eicMessageLoading = false;
      state.eicMessage = action.payload;
      state.error = null;
    },
    setConsentChecked: (state, { payload }: { payload: boolean }) => {
      state.isEicChecked = payload;
    },
    setAttemptedSubmit: (state, { payload }: { payload: boolean }) => {
      state.hasAttemptedSubmit = payload;
    },
    setScrollToTransitionConsent: (state, { payload }: { payload: boolean }) => {
      state.scrollToConsent = payload;
    },
  },
});

export const {
  getPlans,
  setPlans,
  refetchPlans,
  setChangePlanError,
  addPlanToCart,
  submitOrder,
  setOrderSuccess,
  setChangePlanStep,
  setSelectedPlanId,
  saveCart,
  resetChangePlan,
  toggleBroadbandLabels,
  resetHideBroadbandLabels,
  getHasPendingTransition,
  setHasPendingTransition,
  updateDataStillLoading,
  setHasPendingTransitionError,
  setHasActivationError,
  getEicMessages,
  setEicMessages,
  setConsentChecked,
  setScrollToTransitionConsent,
  setAttemptedSubmit,
} = changePlanSlice.actions;

export const selectChangePlan = (state: { changePlan: ChangePlanOffersState }) => state.changePlan;

export const selectChosenPlan = (state: { changePlan: ChangePlanOffersState }) =>
  state.changePlan.planOffersData?.find((plan) => plan.id === state.changePlan.selectedPlanId) as GetPlanOffersPayload;

export const selectFiberDowngradeFee = (state: { changePlan: ChangePlanOffersState }) => {
  const FIBER_DOWNGRADE_FEE_KIND = 'bep.ofm.product-types.transition-fee';
  const FIBER_DOWNGRADE_FEE_ID = '7d01b29a-97f5-4ca9-b604-ac063406c4c7';

  return state.changePlan.cart.prices.oneTime.feesAndDiscounts?.find(
    (item) => item.id === FIBER_DOWNGRADE_FEE_ID || item.kind === FIBER_DOWNGRADE_FEE_KIND
  );
};

export const selectDroppedAddOns = (state: { changePlan: ChangePlanOffersState }) => state.changePlan.droppedAddOns;
